import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Switch, Button, Chip, CircularProgress, Paper, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAppTheme } from '../../hooks/useAppTheme';

interface Task {
  id: number;
  name: string;
  description: string;
  taskType: string;
  cronExpression: string;
  isEnabled: boolean;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastRunDuration: number | null;
  lastRunError: string | null;
  nextDueAt: string | null;
}

// Per-task long-form documentation surfaced via the (i) button. Keep the
// punch-list style — what it does, what it does not touch, and the rough
// cost / safety profile — so the user can decide whether to enable.
const TASK_DOCS: Record<string, { does: string[]; doesNot: string[]; notes?: string[] }> = {
  delta_symbols_import: {
    does: [
      'Pulls the full product list from Delta Exchange.',
      'Adds any symbol that is missing from the local instruments table.',
      'Refreshes mark price on rows that already exist.',
    ],
    doesNot: [
      'Touch user-specific fields (watchlist, trigger levels, investment amounts).',
      'Delete obsolete symbols. Use the Expired Instruments cleanup for that.',
    ],
    notes: [
      'Requires an active Delta API credential. If none exists, the run records "No active Delta Exchange credentials found" and exits without writing anything.',
    ],
  },
  zerodha_symbols_import: {
    does: [
      'Pulls the Zerodha master instruments dump (NSE/BSE/NFO/MCX/CDS/BFO).',
      'Adds the first 3000 missing symbols to instruments table.',
    ],
    doesNot: [
      'Update existing rows.',
      'Touch user-specific preferences.',
    ],
    notes: [
      'Requires an active Zerodha access token (login in the day).',
      'Runs weekly because the master dump rarely changes mid-week and the import is heavy.',
    ],
  },
  expired_instruments_cleanup: {
    does: [
      'Deletes CE / PE / FUT instruments whose expiry is in the past AND that have no open trade.',
    ],
    doesNot: [
      'Delete equities, perpetuals, or any row referenced by an active position.',
      'Delete user_instrument_prefs rows directly (they are removed by FK cascade only when the instrument itself is deleted).',
    ],
  },
  stale_alerts_cleanup: {
    does: [
      'Deletes alerts older than 30 days whose status is ACKNOWLEDGED, EXPIRED, or PARKED.',
    ],
    doesNot: [
      'Delete PENDING or TRIGGERED alerts.',
      'Touch alerts younger than 30 days.',
    ],
  },
  portfolio_snapshot: {
    does: [
      'Captures portfolio value, used / available margin, and unrealized P&L for every user.',
      'Persists the data point as a portfolio_snapshot row used by the dashboard charts.',
    ],
    doesNot: [
      'Place any orders.',
      'Modify trades, alerts, or orders.',
    ],
  },
  db_backup: {
    does: [
      'Writes a JSON backup of trades / alerts / orders / instruments / user settings for every user.',
      'Stores files at DB_BACKUP_DIR (default: the backend logs volume, backend/logs/backups) named backup_<userId>_<timestamp>.json.',
      'Rotates older files — keeps the most recent BACKUP_RETENTION (default 14) per user.',
    ],
    doesNot: [
      'Include Telegram tokens or chat IDs in the dump (sensitive credentials are stripped).',
      'Touch the database — it is a read-only export.',
    ],
    notes: [
      'On-demand "Download Backup Now" in Settings → System still works; it streams the same shape over HTTP for the calling user only.',
      'This is NOT the nightly off-site backup. That one (infra/backup.sh, 01:15 server time, encrypted, to Backblaze B2) copies the WHOLE database and its health is shown in Settings → System → Off-site Backup.',
    ],
  },
};

// Translate a cron expression into a human-readable explanation. Covers the
// shapes the seeded tasks use plus the most common patterns; falls back to
// the raw expression for anything exotic.
function explainCron(cron: string): string {
  if (!cron) return '—';
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;
  const [m, h, dom, mon, dow] = parts;
  const fmtTimeUtc = (mm: string, hh: string) => {
    const mins = parseInt(mm, 10);
    const hours = parseInt(hh, 10);
    if (Number.isFinite(mins) && Number.isFinite(hours)) {
      const istHours = (hours * 60 + mins + 330) / 60;
      const istH = Math.floor((istHours % 24 + 24) % 24);
      const istM = Math.round((istHours - Math.floor(istHours)) * 60);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${pad(hours)}:${pad(mins)} UTC (${pad(istH)}:${pad(istM)} IST)`;
    }
    return `${hh}:${mm} UTC`;
  };
  // every N minutes within an hour range, weekdays
  const everyMin = m.match(/^\*\/(\d+)$/);
  if (everyMin && h.includes('-') && dow.includes('-')) {
    return `Every ${everyMin[1]} minutes during hours ${h} (UTC), on weekdays`;
  }
  // weekly Sunday HH:MM
  if (dow === '0' && dom === '*' && mon === '*' && /^\d+$/.test(m) && /^\d+$/.test(h)) {
    return `Weekly on Sunday at ${fmtTimeUtc(m, h)}`;
  }
  // Daily HH:MM
  if (dom === '*' && mon === '*' && dow === '*' && /^\d+$/.test(m) && /^\d+$/.test(h)) {
    return `Daily at ${fmtTimeUtc(m, h)}`;
  }
  // Hourly
  if (h === '*' && /^\d+$/.test(m) && dom === '*' && mon === '*' && dow === '*') {
    return `Hourly at ${m} minutes past the hour`;
  }
  return `cron: ${cron}`;
}

const ScheduledTasksPanel: React.FC = () => {
  const { C } = useAppTheme();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningId, setRunningId] = useState<number | null>(null);
  const [docTask, setDocTask] = useState<Task | null>(null);
  const [scheduleHelpOpen, setScheduleHelpOpen] = useState(false);

  const fetchTasks = async () => {
    try {
      const res = await api.get<{ success: boolean; data: Task[] }>('/scheduled-tasks');
      if (res.data.success) setTasks(res.data.data);
    } catch {
      toast.error('Failed to load scheduled tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTasks(); }, []);

  const toggleEnabled = async (task: Task) => {
    try {
      const res = await api.put<{ success: boolean; data: Task }>(`/scheduled-tasks/${task.id}`, { isEnabled: !task.isEnabled });
      if (res.data.success) {
        setTasks(prev => prev.map(t => t.id === task.id ? res.data.data : t));
        toast.success(`${task.name} ${res.data.data.isEnabled ? 'enabled' : 'disabled'}`);
      }
    } catch {
      toast.error('Failed to update task');
    }
  };

  const runNow = async (task: Task) => {
    setRunningId(task.id);
    try {
      const res = await api.post<{ success: boolean; data: Task; message?: string }>(`/scheduled-tasks/${task.id}/run`);
      if (res.data.success) {
        setTasks(prev => prev.map(t => t.id === task.id ? res.data.data : t));
        toast.success(res.data.message || `${task.name} completed`);
      } else {
        toast.error(res.data.message || 'Task failed');
        setTasks(prev => prev.map(t => t.id === task.id ? (res.data.data || t) : t));
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Task failed');
      fetchTasks();
    } finally {
      setRunningId(null);
    }
  };

  const statusChip = (status: string | null) => {
    if (!status) return null;
    const color = status === 'SUCCESS' ? 'success' : status === 'FAILED' ? 'error' : 'warning';
    return <Chip label={status} size="small" color={color} sx={{ fontSize: '0.65rem', height: 20 }} />;
  };

  const formatDuration = (ms: number | null) => {
    if (ms == null) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatTime = (iso: string | null) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const formatNext = (iso: string | null, enabled: boolean) => {
    if (!enabled) return <Typography variant="caption" sx={{ color: 'text.disabled' }}>disabled</Typography>;
    if (!iso) return <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>;
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return <Typography variant="caption" sx={{ color: 'warning.main' }}>imminent</Typography>;
    const totalMins = Math.floor(ms / 60000);
    const days = Math.floor(totalMins / 1440);
    const hours = Math.floor((totalMins % 1440) / 60);
    const mins = totalMins % 60;
    let rel = '';
    if (days > 0) rel = `in ${days}d ${hours}h`;
    else if (hours > 0) rel = `in ${hours}h ${mins}m`;
    else rel = `in ${mins}m`;
    return (
      <Box>
        <Typography variant="caption" sx={{ display: 'block' }}>{formatTime(iso)}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>{rel}</Typography>
      </Box>
    );
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Scheduled Tasks</Typography>
        <Tooltip title="How the schedule column works" arrow>
          <IconButton size="small" onClick={() => setScheduleHelpOpen(true)}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
        Server-side housekeeping. Toggle <i>Enabled</i> to start/stop the cron registration; "Run Now" fires the task on demand. The (i) icon next to each row explains exactly what that task does and does not touch.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Task</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Schedule</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="center">Enabled</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Last Run</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Next Run</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tasks.map(task => (
            <TableRow key={task.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <Typography variant="body2" fontWeight={600}>{task.name}</Typography>
                  <Tooltip title="What this task does (and does not do)" arrow>
                    <IconButton size="small" onClick={() => setDocTask(task)} sx={{ p: 0.2 }}>
                      <InfoOutlined sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>{task.description}</Typography>
              </TableCell>
              <TableCell>
                <Tooltip title={task.cronExpression || ''} arrow placement="top">
                  <Box>
                    <Typography variant="caption" sx={{ display: 'block' }}>{explainCron(task.cronExpression)}</Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'text.secondary' }}>{task.cronExpression}</Typography>
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell align="center">
                <Switch size="small" checked={task.isEnabled} onChange={() => toggleEnabled(task)} />
              </TableCell>
              <TableCell>
                <Typography variant="caption">{formatTime(task.lastRunAt)}</Typography>
              </TableCell>
              <TableCell>
                {formatNext(task.nextDueAt, task.isEnabled)}
              </TableCell>
              <TableCell>{statusChip(task.lastRunStatus)}</TableCell>
              <TableCell>
                <Typography variant="caption">{formatDuration(task.lastRunDuration)}</Typography>
              </TableCell>
              <TableCell align="center">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={runningId === task.id ? <CircularProgress size={12} /> : <PlayArrowIcon sx={{ fontSize: 14 }} />}
                  disabled={runningId === task.id}
                  onClick={() => runNow(task)}
                  sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                >
                  {runningId === task.id ? 'Running...' : 'Run Now'}
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {tasks.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography variant="caption" color="text.secondary">No scheduled tasks configured</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {tasks.some(t => t.lastRunError) && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ color: C.bear, fontWeight: 700 }}>Recent Errors:</Typography>
          {tasks.filter(t => t.lastRunError).map(t => (
            <Typography key={t.id} variant="caption" sx={{ display: 'block', color: C.bear, fontSize: '0.7rem', mt: 0.5 }}>
              {t.name}: {t.lastRunError}
            </Typography>
          ))}
        </Box>
      )}

      {/* Schedule cheat-sheet */}
      <Dialog open={scheduleHelpOpen} onClose={() => setScheduleHelpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Schedule format</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The Schedule column uses a five-field cron expression — same as Linux <code>crontab</code>:
          </Typography>
          <Box sx={{ fontFamily: 'monospace', fontSize: '0.78rem', bgcolor: 'action.hover', p: 1, borderRadius: 1, mb: 2 }}>
            minute  hour  day-of-month  month  day-of-week<br />
            (0–59)  (0–23)  (1–31)        (1–12) (0–6, Sun=0)
          </Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}><b>All times are UTC.</b> The column also shows the IST equivalent in parentheses.</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>Examples:</Typography>
          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            <li><code>0 1 * * *</code> — daily at 01:00 UTC (06:30 IST)</li>
            <li><code>30 19 * * *</code> — daily at 19:30 UTC (01:00 IST next day)</li>
            <li><code>0 0 * * 0</code> — weekly on Sunday at 00:00 UTC</li>
            <li><code>*/15 3-10 * * 1-5</code> — every 15 min between 03:00–10:00 UTC, weekdays</li>
          </Box>
          <Typography variant="body2" sx={{ mt: 2 }}>
            <b>Next Run</b> column shows the absolute timestamp the cron schedule will next fire, plus a relative countdown. <i>disabled</i> means the toggle is off so no firing is registered. <i>imminent</i> means the next-due timestamp has just passed and a run is about to start (or just finished).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleHelpOpen(false)} variant="contained">Got it</Button>
        </DialogActions>
      </Dialog>

      {/* Per-task documentation */}
      <Dialog open={!!docTask} onClose={() => setDocTask(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{docTask?.name}</DialogTitle>
        <DialogContent dividers>
          {docTask && (() => {
            const docs = TASK_DOCS[docTask.taskType];
            return (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>{docTask.description}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                  Schedule: <b>{explainCron(docTask.cronExpression)}</b>
                  {' · '}
                  <code style={{ fontSize: '0.75em' }}>{docTask.cronExpression}</code>
                </Typography>
                {docs ? (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, color: C.bull }}>Does</Typography>
                    <Box component="ul" sx={{ pl: 3, m: 0 }}>
                      {docs.does.map((d, i) => <li key={i}><Typography variant="body2">{d}</Typography></li>)}
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, color: C.bear }}>Does NOT</Typography>
                    <Box component="ul" sx={{ pl: 3, m: 0 }}>
                      {docs.doesNot.map((d, i) => <li key={i}><Typography variant="body2">{d}</Typography></li>)}
                    </Box>
                    {docs.notes && docs.notes.length > 0 && (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, color: 'text.secondary' }}>Notes</Typography>
                        <Box component="ul" sx={{ pl: 3, m: 0 }}>
                          {docs.notes.map((d, i) => <li key={i}><Typography variant="body2">{d}</Typography></li>)}
                        </Box>
                      </>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">No additional documentation available for this task.</Typography>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocTask(null)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ScheduledTasksPanel;
